import { memo, useEffect, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Button, Drawer, Stack, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import { drawerWidth } from 'store/constant';
import { getLatestVerion } from 'store/slices/releases';
import { Link } from 'react-router-dom';
import MiniDrawerStyled from './MiniDrawerStyled';

// ==============================|| SIDEBAR DRAWER ||============================== //

interface SidebarProps {
    window?: Window;
}

const Sidebar = ({ window }: SidebarProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));

    const { drawerOpen } = useSelector((state) => state.menu);
    const { appVersion } = useSelector((state) => state.releases);

    useEffect(() => {
        dispatch(getLatestVerion());
    }, [dispatch]);

    const logo = useMemo(
        () => (
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
                    <LogoSection />
                </Box>
            </Box>
        ),
        []
    );

    const drawer = useMemo(
        () => (
            <PerfectScrollbar
                component="div"
                style={{
                    height: !matchUpMd ? 'calc(100vh - 56px)' : 'calc(100vh - 88px)',
                    paddingLeft: drawerOpen ? '16px' : 0,
                    paddingRight: drawerOpen ? '16px' : 0,
                    marginTop: drawerOpen ? 0 : '20px'
                }}
            >
                <MenuList />
                {appVersion && (
                    <Stack direction="row" justifyContent="center" sx={{ my: 3 }}>
                        <Button size="small" sx={{ borderRadius: 5 }} component={Link} target="_blank" to="/loolu/releases">
                            {appVersion}
                        </Button>
                    </Stack>
                )}
            </PerfectScrollbar>
        ),
        [appVersion, matchUpMd, drawerOpen]
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    return (
        <Box
            component="nav"
            sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : drawerOpen ? 'auto' : '0px' }}
            aria-label="mailbox folders"
        >
            {drawerOpen ? (
                <Drawer
                    container={container}
                    variant={matchUpMd ? 'persistent' : 'temporary'}
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => dispatch(openDrawer(!drawerOpen))}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            background: theme.palette.background.default,
                            color: theme.palette.text.primary,
                            borderRight: 'none',
                            [theme.breakpoints.up('md')]: {
                                top: '88px'
                            }
                        }
                    }}
                    ModalProps={{ keepMounted: true }}
                    color="inherit"
                >
                    {drawerOpen && logo}
                    {drawer}
                </Drawer>
            ) : (
                <MiniDrawerStyled
                    variant="permanent"
                    open={drawerOpen}
                    sx={{
                        '& .MuiDrawer-paper': {
                            [theme.breakpoints.up('md')]: {
                                top: '88px'
                            },
                            [theme.breakpoints.down('md')]: {
                                display: 'none'
                            }
                        }
                    }}
                >
                    {logo}
                    {drawer}
                </MiniDrawerStyled>
            )}
        </Box>
    );
};

export default memo(Sidebar);
